import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/base/SEOHead';
import { menuData } from '../../mocks/menuData';

const MenuPage = () => {
  const foodCategories = menuData.filter(c =>
    ['ov-tailgating','fry-boats','wings','quesadillas','lighter-fare','steak-seafood','burgers','sandwiches','baskets','sides-desserts'].includes(c.id)
  );
  const drinkCategories = menuData.filter(c =>
    ['ov-dranks','spirits'].includes(c.id)
  );

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Menu",
    "name": "Ocean View Inn & Sports Pub Menu",
    "description": "Full menu featuring fresh Florida Keys seafood, classic American fare, signature cocktails and island favorites",
    "hasMenuSection": menuData.map(category => ({
      "@type": "MenuSection",
      "name": category.name,
      "description": category.description,
      "hasMenuItem": category.items.map(item => ({
        "@type": "MenuItem",
        "name": item.name,
        "description": item.description,
        "offers": {
          "@type": "Offer",
          "price": item.price,
          "priceCurrency": "USD"
        }
      }))
    }))
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Menu Islamorada Restaurant | Ocean View Inn & Sports Pub Florida Keys"
        description="Explore the full menu at Ocean View Inn & Sports Pub in Islamorada, Florida Keys. Fresh seafood, classic American fare, appetizers, sandwiches, entrees, and more. Dine-in or takeout available."
        keywords="Islamorada restaurant menu, Florida Keys dining, seafood menu, Ocean View Inn food, restaurant menu"
        canonical={`${import.meta.env.VITE_SITE_URL || 'https://example.com'}/menu`}
        ogTitle="Full Menu | Ocean View Inn & Sports Pub Islamorada"
        ogDescription="Explore our full menu featuring fresh Florida Keys seafood, classic American fare, and island favorites"
        ogImage="https://public.readdy.ai/ai/img_res/ad4c373ede89f874856e39741d76c9b1.jpg"
        ogUrl={`${import.meta.env.VITE_SITE_URL || 'https://example.com'}/menu`}
        structuredData={structuredData}
        geoPosition="24.9470979;-80.6019239"
        geoRegion="US-FL"
        geoPlacename="Islamorada, Florida"
      />
      <Header />
      <main className="pt-20">
        {/* Hero */}
        <section className="relative h-[400px] flex items-center justify-center overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center bg-top"
            style={{
              backgroundImage: 'url(https://readdy.ai/api/search-image?query=Beautiful%20restaurant%20menu%20spread%20with%20fresh%20seafood%20burgers%20and%20gourmet%20dishes%20on%20rustic%20wooden%20table%2C%20warm%20lighting%2C%20professional%20food%20photography%2C%20appetizing%20presentation%2C%20Florida%20Keys%20coastal%20dining%20atmosphere&width=1200&height=400&seq=26&orientation=landscape)',
            }}
          >
            <div className="absolute inset-0 bg-slate-900/70"></div>
          </div>
          <div className="relative z-10 text-center text-white max-w-4xl mx-auto px-4">
            <h1 className="text-5xl md:text-6xl font-bold mb-4 font-serif">Our Menu</h1>
            <p className="text-xl text-slate-200">
              Seafood, hand-made burgers, steaks and Keys specialties since 1996
            </p>
          </div>
        </section>

        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

            {/* Intro */}
            <div className="max-w-3xl mx-auto text-center mb-16">
              <h2 className="text-4xl font-bold text-slate-900 mb-6 font-serif">
                Explore Our Menu
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                From fresh Florida Keys seafood to hand-made burgers, signature cocktails and island favorites — everything prepared fresh to order.
              </p>
            </div>

            {/* Food Categories */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-slate-800 mb-8 flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-restaurant-line text-amber-600 text-2xl"></i>
                </span>
                Food
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {foodCategories.map((category, index) => (
                  <Link
                    key={index}
                    to={`/menu/${category.slug}`}
                    className="bg-white rounded-2xl shadow-md overflow-hidden hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 group border border-slate-100 cursor-pointer"
                  >
                    <div className="overflow-hidden">
                      <img
                        src={category.image}
                        alt={category.name}
                        className="w-full h-40 object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-5">
                      <h3 className="text-lg font-bold text-slate-900 mb-1 group-hover:text-amber-600 transition-colors">{category.name}</h3>
                      <p className="text-slate-500 text-sm mb-3 line-clamp-2">{category.description}</p>
                      <div className="flex items-center text-amber-600 text-sm font-semibold group-hover:translate-x-1 transition-transform duration-300">
                        <span>View Items</span>
                        <i className="ri-arrow-right-line ml-1"></i>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Drinks Categories */}
            <div className="mb-16">
              <h3 className="text-2xl font-bold text-slate-800 mb-8 flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-goblet-line text-amber-600 text-2xl"></i>
                </span>
                Drinks
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {drinkCategories.map((category, index) => (
                  <Link
                    key={index}
                    to={`/menu/${category.slug}`}
                    className="bg-white rounded-2xl shadow-md overflow-hidden hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 group border border-slate-100 flex cursor-pointer"
                  >
                    <div className="overflow-hidden w-48 shrink-0">
                      <img
                        src={category.image}
                        alt={category.name}
                        className="w-48 h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-6 flex flex-col justify-center">
                      <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-amber-600 transition-colors">{category.name}</h3>
                      <p className="text-slate-500 text-sm mb-4 line-clamp-3">{category.description}</p>
                      <div className="flex items-center text-amber-600 text-sm font-semibold group-hover:translate-x-1 transition-transform duration-300">
                        <span>View Items</span>
                        <i className="ri-arrow-right-line ml-1"></i>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Order CTA */}
            <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-8 md:p-12 text-white">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div>
                  <h3 className="text-3xl font-bold mb-4">Order Online or Dine In</h3>
                  <p className="text-slate-300 mb-6 leading-relaxed">
                    Enjoy our full menu in our sports pub, on our outdoor patio, or order online for pickup. Serving lunch, dinner, and late-night bites daily.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <a
                      href="https://www.toasttab.com/the-ocean-view-84500-overseas-hwy"
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="inline-flex items-center justify-center px-6 py-3 bg-amber-500 hover:bg-amber-400 text-white rounded-lg font-semibold hover:shadow-xl transform hover:scale-105 transition-all duration-300 whitespace-nowrap"
                    >
                      <i className="ri-shopping-bag-line mr-2"></i>
                      Order Online
                    </a>
                    <a
                      href="tel:+13056648052"
                      className="inline-flex items-center justify-center px-6 py-3 bg-white text-slate-900 rounded-lg font-semibold hover:bg-slate-100 transition-colors whitespace-nowrap"
                    >
                      <i className="ri-phone-line mr-2"></i>
                      Call to Order
                    </a>
                  </div>
                </div>
                <div className="bg-slate-800 rounded-xl p-6">
                  <h4 className="font-semibold mb-4 text-lg">Kitchen Hours</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center pb-2 border-b border-slate-700">
                      <span className="text-slate-300">Monday – Thursday</span>
                      <span className="font-medium">11 AM – 9 PM</span>
                    </div>
                    <div className="flex justify-between items-center pb-2 border-b border-slate-700">
                      <span className="text-slate-300">Friday – Saturday</span>
                      <span className="font-medium">11 AM – 10 PM</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Sunday</span>
                      <span className="font-medium">11 AM – 9 PM</span>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-slate-700 text-sm text-amber-400 font-medium">
                    <i className="ri-sun-line mr-1"></i> Happy Hour: Mon–Fri 4–6 PM
                  </div>
                </div>
              </div>
            </div>

            {/* History */}
            <div className="mt-16 bg-amber-50 rounded-2xl p-8 text-center border border-amber-100">
              <h3 className="text-2xl font-bold text-slate-900 mb-4 font-serif">A Brief Semi-Factual History of the "O.V."</h3>
              <p className="text-slate-700 leading-relaxed max-w-4xl mx-auto">
                The "O.V." once boasted a sweeping ocean view before Hurricane Donna reshaped the Keys. Rebuilt in its current home, it holds the oldest liquor license in the Florida Keys and has long been a haven for smugglers and legends along trails like Capt. Larry. Family owned and operated since 1996 by former NFL player and Super Bowl champion Gary Dunn, we invite you to sit back, relax, and enjoy the World Famous O.V.
              </p>
              <div className="mt-6 text-sm text-slate-600 space-y-1">
                <p>
                  Host Your Event with Us! &nbsp;Call:{' '}
                  <a href="tel:3056648052" className="text-amber-600 font-semibold">305-664-8052</a>
                  &nbsp;|&nbsp; Email:{' '}
                  <a href="mailto:resinfo@theocean-view.com" className="text-amber-600 font-semibold">resinfo@theocean-view.com</a>
                </p>
                <p>Follow us: <strong>FB</strong> @theoceanviewsportspub &nbsp;|&nbsp; <strong>IG</strong> @theoceanviewinn&amp;pub</p>
              </div>
            </div>

          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default MenuPage;
